import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import client from "../lib/api";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import AffinityBadge from "../components/AffinityBadge";
import PremiumBadge from "../components/PremiumBadge";
import VerifiedBadge, { isVerified } from "../components/VerifiedBadge";
import { Eye } from "lucide-react";

const PLACEHOLDER = "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=400";

const fmtDate = (s) => {
  if (!s) return "";
  try {
    return new Date(s).toLocaleString("pt-PT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return s; }
};

const ProfileViews = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await client.get("/profile-views");
        setData(r.data.views || []);
      } catch (err) {
        // Don't surface a UI error: the views page is read-only and a
        // failure here just means an empty list. Logged so it can be
        // tracked in browser DevTools / Sentry.
        // eslint-disable-next-line no-console
        console.warn("Failed to load profile views:", err);
      }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#0C0E12]" data-testid="views-page">
      <Navbar />
      <main className="max-w-4xl mx-auto px-6 md:px-12 py-10">
        <div className="flex items-center gap-2">
          <Eye className="w-4 h-4 text-[#C5A880]" />
          <span className="label-uppercase">Quem te visitou</span>
        </div>
        <h1 className="font-display text-3xl md:text-5xl mt-3 mb-2">Quem viu o teu perfil</h1>
        <p className="text-[#A29F9B] mb-10 max-w-2xl">
          Visualizações dos últimos 90 dias. Quem aparece mais vezes, demonstra mais interesse genuíno.
        </p>

        {loading && <div className="text-[#A29F9B]">A carregar...</div>}

        {!loading && (data || []).length === 0 && (
          <div className="bg-[#161920] border border-white/5 rounded-2xl p-12 text-center" data-testid="views-empty">
            <Eye className="w-8 h-8 text-[#C5A880] mx-auto mb-4" />
            <h3 className="font-display text-2xl mb-2">Ainda sem visualizações</h3>
            <p className="text-[#A29F9B]">Atrai mais visitas com fotos genuínas e bio detalhada.</p>
          </div>
        )}

        {!loading && (data || []).length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="views-list">
            {data.map((v) => (
              <Link key={v.viewer_id} to={`/profile/${v.viewer_id}`}
                className="group bg-[#161920] border border-white/5 rounded-2xl overflow-hidden hover:border-[#C5A880]/40 transition-colors"
                data-testid={`view-card-${v.viewer_id}`}>
                <div className="aspect-[4/5] overflow-hidden relative">
                  <img src={v.profile.photos?.[0] || PLACEHOLDER} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute top-3 right-3 flex flex-col items-end gap-1.5">
                    {isVerified(v.profile) && <VerifiedBadge size="sm" />}
                    {v.profile.is_premium && <PremiumBadge />}
                  </div>
                  <div className="absolute bottom-3 left-3">
                    <AffinityBadge tier={v.tier} score={v.compatibility?.overall} size="sm" short />
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-display text-lg">{v.profile.name}, {v.profile.age}</h3>
                    <span className="text-[#C5A880] text-sm">{v.compatibility?.overall}%</span>
                  </div>
                  <div className="text-xs text-[#A29F9B] mt-2 flex items-center justify-between">
                    <span>{fmtDate(v.last_view_at)}</span>
                    {v.view_count > 1 && (
                      <span className="text-[#C5A880]">{v.view_count}× visitas</span>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default ProfileViews;
